<template>
  <div>
    <BittsInput
      v-model="search"
      class="my-8"
      :name="name"
      :placeholder="placeholder"
      size="small"
    >
      <template #prefix>
        <FontAwesomeIcon
          :icon="['fak', 'search']"
          :style="{
            height: '14px',
            width: '14px',
            color: 'currentColor',
          }"
          class="text-info-accent pr-6"
        />
      </template>
    </BittsInput>
    <BittsBaseTree
      :is-disabled
      :node-data="filteredNodeData"
      :expand-all="expandAll"
      :force-expanded-ids="matchExpandedIds"
      :highlight-term="normalizedTerm"
      @update="onUpdate"
    >
      <template #node-checkbox="slotProps">
        <slot name="node-checkbox" v-bind="slotProps" />
      </template>
      <template #node-prefix="{ node }">
        <slot name="node-prefix" :node="node" />
      </template>
      <template #node-content="{ node, highlightTerm }">
        <slot
          name="node-content"
          :node="node"
          :highlight-term="highlightTerm"
        />
      </template>
      <template #node-suffix="{ node }">
        <slot name="node-suffix" :node="node"></slot>
      </template>
    </BittsBaseTree>
  </div>
</template>

<script setup lang="ts" generic="TMetaData">
import { refDebounced } from '@vueuse/core';

import BittsInput from '../BittsInput/BittsInput.vue';

import BittsBaseTree, { TreeNodeType } from './BittsBaseTree.vue';
import { useTreeSearch } from './useTreeSearch';

type Node = TreeNodeType<TMetaData>;

const {
  nodeData,
  isDisabled = false,
  expandAll = false,
  name = 'search',
  placeholder = 'Search',
  searchDebounce = 200,
  autoExpandLimit,
} = defineProps<{
  isDisabled?: boolean;
  nodeData: Node[];
  expandAll?: boolean;
  name?: string;
  placeholder?: string;
  searchDebounce?: number;
  autoExpandLimit?: number;
}>();

const emit = defineEmits<{
  update: [value: Node[], updatedNodeId: number | string];
}>();

defineSlots<{
  'node-checkbox'?(props: {
    node: Node;
    checked: boolean | undefined;
    indeterminate: boolean;
    onInput: (checked: boolean) => void;
    disabled: boolean;
  }): unknown;
  'node-prefix'?(props: { node: Node }): unknown;
  'node-content'?(props: { node: Node; highlightTerm: string }): unknown;
  'node-suffix'?(props: { node: Node }): unknown;
}>();

const search = defineModel<string>('search', { default: '' });

const debouncedSearch = refDebounced(search, searchDebounce);

const {
  filteredNodeData,
  matchExpandedIds,
  normalizedTerm,
  findNode,
  applyCheckedChange,
} = useTreeSearch(() => nodeData, debouncedSearch, { autoExpandLimit });

function onUpdate(updatedVisibleNodes: Node[], updatedNodeId: number | string) {
  const changed = findNode(updatedVisibleNodes, updatedNodeId);
  emit(
    'update',
    applyCheckedChange(nodeData, updatedNodeId, changed?.checked ?? false),
    updatedNodeId,
  );
}
</script>
